import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import BookCover from '../components/BookCover';
import { Toaster, toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Trash2, BookOpen, ShoppingCart, Loader2, ArrowLeft, X } from 'lucide-react';
import { loginWithGoogle } from '../utils/auth';

import { BACKEND_URL, API } from '../utils/api';
import { authFetch, clearToken } from '../utils/session';
const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + "/api/static";
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

class PayPalErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error('PayPal error:', err); }
  render() {
    if (this.state.hasError) return <p className="text-xs text-red-500 text-center py-2">Error al cargar PayPal. Recarga la página.</p>;
    return this.props.children;
  }
}

const CartPage = () => {
  const navigate = useNavigate();
  const [cartSlugs, setCartSlugs] = useState([]);
  const [books, setBooks] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [giftSlugs, setGiftSlugs] = useState([]);
  const [giftMessage, setGiftMessage] = useState('');
  const [giftResult, setGiftResult] = useState(null);

  useEffect(() => {
    Promise.all([
      authFetch(`${API}/auth/me`, {  }).then(r => r.ok ? r.json() : null),
      authFetch(`${API}/user/cart`, {  }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/books`).then(r => r.json()),
    ]).then(([u, cart, bks]) => {
      setUser(u);
      setCartSlugs(cart || []);
      const mapped = (bks || []).map(b => ({ ...b, id: b.book_id || b.slug, coverUrl: `${R2_URL}/books/${b.imageFolder}/cover.webp` }));
      setAllBooks(mapped);
      setBooks(mapped.filter(b => (cart || []).includes(b.slug)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const removeFromCart = async (slug) => {
    await authFetch(`${API}/user/cart/${slug}`, { method: 'DELETE' });
    setCartSlugs(prev => prev.filter(s => s !== slug));
    setBooks(prev => prev.filter(b => b.slug !== slug));
  };

  const subtotal = books.reduce((s, b) => s + (b.price || 0), 0);
  const discountBooks = Math.max(0, books.length - 3); // 5% off from 4th book
  const discount = discountBooks > 0 ? books.slice(3).reduce((s, b) => s + (b.price || 0) * 0.05, 0) : 0;
  const total = Math.round((subtotal - discount) * 100) / 100;

  const handleCheckout = async (paypalOrderId) => {
    const res = await authFetch(`${API}/user/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paypal_order_id: paypalOrderId, gift_slugs: giftSlugs, gift_message: giftMessage }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.gift_id) {
        setGiftResult({ id: data.gift_id, url: `${window.location.origin}/regalo/${data.gift_id}` });
        toast.success('¡Compra exitosa!');
      } else {
        toast.success('¡Compra exitosa! Tus libros están en Mi Biblioteca');
        setTimeout(() => navigate('/biblioteca'), 1500);
      }
      setCartSlugs([]); setBooks([]);
    } else {
      toast.error('Error al procesar la compra');
    }
  };

  if (loading) return <div className="min-h-dvh flex items-center justify-center"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;

  if (!user) {
    return (
      <div className="min-h-dvh bg-background font-sans">
        <Header onSearchClick={() => navigate('/')} searchText="" />
        <div className="max-w-md mx-auto px-6 pt-20 text-center">
          <ShoppingCart className="size-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm mb-4">Inicia sesión para ver tu carrito</p>
          <button onClick={() => loginWithGoogle('/carrito')} className="h-10 px-5 rounded-xl bg-foreground text-background text-sm font-medium">Iniciar sesión con Google</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background font-sans">
      <Toaster position="top-center" />
      <Header onSearchClick={() => navigate('/')} searchText="" />
      <div className="max-w-2xl mx-auto px-6 pt-6 pb-16">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="size-4" /> Volver
        </button>
        <h1 className="text-xl font-bold mb-6">Mi Carrito</h1>

        {/* Gift onboarding - shows once */}
        {books.length > 0 && !giftResult && !localStorage.getItem('gift_onboarding_seen') && (
          <div className="mb-6 bg-gradient-to-r from-amber-50/80 to-orange-50/80 border border-amber-200/40 rounded-2xl p-5 relative">
            <button onClick={() => { localStorage.setItem('gift_onboarding_seen', 'true'); document.getElementById('gift-onboarding')?.remove(); }} className="absolute top-3 right-3 text-amber-400 hover:text-amber-600 transition-colors">
              <X className="size-4" />
            </button>
            <div id="gift-onboarding">
              <p className="text-sm font-semibold text-amber-800 mb-1">¿Sabías que puedes regalar un librito? ✨</p>
              <p className="text-xs text-amber-700/70 leading-relaxed">Toca <span className="font-medium">"Para regalar"</span> en cualquier librito y al pagar recibirás un enlace mágico para compartir. ¡Quien lo abra encontrará su regalo listo para leer!</p>
            </div>
          </div>
        )}
        {giftResult ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🎁</div>
            <h2 className="text-lg font-bold mb-2">¡Regalo listo!</h2>
            <p className="text-sm text-muted-foreground mb-6">Comparte este enlace con quien quieras regalar</p>
            <div className="bg-secondary rounded-xl p-4 mb-4 max-w-sm mx-auto">
              <p className="text-xs text-muted-foreground mb-2 break-all">{giftResult.url}</p>
              <button onClick={() => { navigator.clipboard.writeText(giftResult.url); toast.success('Enlace copiado'); }} className="w-full h-9 rounded-lg bg-foreground text-background text-sm font-medium">Copiar enlace</button>
            </div>
            <a href={`https://wa.me/?text=${encodeURIComponent(`¡Te regalo estos libritos cristianos! 🎁 Ábrelo aquí: ${giftResult.url}`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[#25D366] text-white text-sm font-medium">Compartir por WhatsApp</a>
            <div className="mt-6"><button onClick={() => navigate('/')} className="text-sm text-muted-foreground hover:underline">Volver a la tienda</button></div>
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="size-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Tu carrito está vacío</p>
            <button onClick={() => navigate('/')} className="mt-4 text-sm text-foreground font-medium hover:underline">Explorar libros</button>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-8">
              {books.map(book => (
                <div key={book.slug} className="p-3 rounded-xl border border-border">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-16 rounded-lg overflow-hidden shrink-0"><BookCover book={book} hideTitle /></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold truncate">{book.title}</h3>
                      <p className="text-xs text-emerald-600 font-medium">${book.price?.toFixed(2)} USD</p>
                    </div>
                    <button onClick={() => removeFromCart(book.slug)} className="p-2 text-muted-foreground/40 hover:text-red-500 transition-colors"><Trash2 className="size-4" /></button>
                  </div>
                  <div className="flex items-center gap-2 mt-2 ml-16">
                    <button onClick={() => setGiftSlugs(prev => prev.includes(book.slug) ? prev.filter(s => s !== book.slug) : [...prev, book.slug])}
                      className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${giftSlugs.includes(book.slug) ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-border text-muted-foreground hover:border-foreground/20'}`}>
                      {giftSlugs.includes(book.slug) ? '🎁 Para regalar' : 'Para mí'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {giftSlugs.length > 0 && (
              <div className="mb-6 bg-amber-50/50 border border-amber-200/50 rounded-xl p-4">
                <p className="text-xs font-medium text-amber-700 mb-2">🎁 Mensaje para el regalo (opcional)</p>
                <input value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} maxLength={200} placeholder="Ej: Para mi sobrino con amor" className="w-full h-9 px-3 rounded-lg bg-white border border-amber-200/50 text-sm outline-none focus:ring-2 focus:ring-amber-300/30 placeholder:text-muted-foreground/40" />
              </div>
            )}
            <div className="border-t border-border pt-4">
              {discountBooks > 0 && (
                <div className="mb-4 bg-emerald-50 border border-emerald-200/60 rounded-xl p-3">
                  <p className="text-xs font-semibold text-emerald-700 mb-1">5% de descuento aplicado</p>
                  <p className="text-[11px] text-emerald-600">A partir del 4to librito obtienes 5% de descuento en cada uno adicional</p>
                </div>
              )}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="text-sm text-muted-foreground">${subtotal.toFixed(2)}</span>
                </div>
                {discountBooks > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-emerald-600">Descuento ({discountBooks} libro{discountBooks > 1 ? 's' : ''} con 5%)</span>
                    <span className="text-sm text-emerald-600">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="text-sm font-semibold">Total</span>
                  <span className="text-lg font-bold text-emerald-600">${total.toFixed(2)} USD</span>
                </div>
              </div>
              {PAYPAL_CLIENT_ID ? (
                <PayPalErrorBoundary>
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD' }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 45 }}
                      createOrder={(data, actions) => actions.order.create({ purchase_units: [{ amount: { value: total.toFixed(2) }, description: `Museo de la Fe - ${books.length} libros` }] })}
                      onApprove={async (data, actions) => { const order = await actions.order.capture(); await handleCheckout(order.id); }}
                      onError={() => toast.error('Error al procesar el pago')}
                    />
                    <p className="text-[10px] text-muted-foreground/40 text-center mt-2">Pago seguro con PayPal</p>
                  </PayPalScriptProvider>
                </PayPalErrorBoundary>
              ) : (
                <button onClick={() => handleCheckout('mock')} className="w-full h-12 rounded-xl bg-foreground text-background font-semibold">Pagar ${total.toFixed(2)}</button>
              )}
              <button onClick={() => navigate('/')} className="w-full mt-3 h-10 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors">
                Continuar comprando
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartPage;
